import React from "react";
import { Box, BreadcrumbInput, Menu, MenuPopup, MenuTrigger } from "@salestrak/ui";

import { EntityBreadcrumbLink, EntityVisual } from "#app/components/entity";
import { DROPDOWN_MENU_PARTS } from "#app/components/menus/menu-parts";
import {
  renderTeamMenuItems,
  useTeamMenuActions,
} from "#app/components/teams/team-menu-items";
import type { WorkspaceTeam } from "#app/lib/data";
import { TEAM_NAME_MAX_LENGTH } from "#app/lib/team-handle";
import { teamRefToEntity } from "#app/lib/entity-visuals";

/**
 * The team masthead crumb as a dropdown: the shared team roster
 * (`renderTeamMenuItems`) on the crumb's press — the workflow shell's grammar
 * (`WorkflowBreadcrumbMenu`), so the two entity shells edit their identity
 * the same way. This crumb IS the current page, so a routing press has
 * nowhere to go; the trailing chevron announces the menu instead. The menu
 * offers rename (inline, in the crumb's own slot), copy link, color & icon,
 * and settings — rows the viewer lacks permission for stay visible but
 * disabled, with the reason on hover.
 */

export type TeamBreadcrumbMenuProps = {
  team: WorkspaceTeam;
};

export function TeamBreadcrumbMenu({ team }: TeamBreadcrumbMenuProps) {
  const teamId = team.teamId;
  const actions = useTeamMenuActions();
  const entity = teamRefToEntity(team);

  const [renaming, setRenaming] = React.useState(false);
  // Controlled so Rename can begin after the menu finishes closing.
  const [menuOpen, setMenuOpen] = React.useState(false);
  // The rename starts AFTER the menu finishes closing, or the menu's focus
  // return steals the input's focus.
  const pendingRenameRef = React.useRef(false);

  if (renaming) {
    // The crumb's own metrics and glyph, with the draft in the brand ink —
    // entering the edit moves nothing but the ring.
    return (
      <BreadcrumbInput
        aria-label="Team name"
        defaultValue={team.teamName}
        maxLength={TEAM_NAME_MAX_LENGTH}
        onCancel={() => setRenaming(false)}
        onCommit={(next) => {
          setRenaming(false);
          void actions.rename(teamId, next);
        }}
        visual={<EntityVisual entity={entity} size="2xs" bareIconSize="md" />}
      />
    );
  }

  return (
    <Menu
      open={menuOpen}
      onOpenChange={setMenuOpen}
      onOpenChangeComplete={(open) => {
        if (open || !pendingRenameRef.current) return;
        pendingRenameRef.current = false;
        setRenaming(true);
      }}
    >
      {/* Trigger wraps from OUTSIDE via render: the wrapper owns the press,
        * so the crumb itself stays a non-button (no interactive nesting) —
        * `dropdown` is what makes it read as the trigger's face: hover wash
        * held while the menu is open, pointer cursor, trailing chevron. The
        * wrapper is a Box, so the trigger is declared non-native: that gives
        * it the button role its aria-expanded needs. */}
      <MenuTrigger
        nativeButton={false}
        render={<Box align="center" direction="row" minWidth={0} />}
      >
        <EntityBreadcrumbLink entity={entity} active dropdown />
      </MenuTrigger>
      <MenuPopup align="start" side="bottom" width="action">
        {renderTeamMenuItems({
          parts: DROPDOWN_MENU_PARTS,
          team,
          actions,
          onRename: () => {
            pendingRenameRef.current = true;
          },
        })}
      </MenuPopup>
    </Menu>
  );
}
